import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { ChevronLeft, ChevronRight, Settings, Plus, Trash2, Gauge } from "lucide-react";

export const LaborComparison = ({ propertyId, lockedRef }) => {
  const { user } = useAuth();
  const canEditStd = can(user, ["admin", "regional"]);
  const canOverride = can(user, ["admin", "regional", "gm", "finance"]);
  const [ref, setRef] = useState(lockedRef || null);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({}); // {department: value}
  const [stdOpen, setStdOpen] = useState(false);
  const [stds, setStds] = useState([]);

  // When embedded on the Payroll Sheet, follow the sheet's pay period.
  useEffect(() => { if (lockedRef !== undefined) setRef(lockedRef); }, [lockedRef]);

  const load = useCallback(() => {
    if (!propertyId) return;
    const params = { property_id: propertyId };
    if (ref) params.ref = ref;
    api.get("/payroll-sheet/labor", { params }).then((r) => { setData(r.data); setEdit({}); }).catch(() => setData(null));
  }, [propertyId, ref]);

  useEffect(() => { load(); }, [load]);

  const saveOverride = async (dep, val) => {
    try {
      await api.post("/payroll-sheet/labor-override", { property_id: propertyId, start: data.start, department: dep, estimated_hours: val === "" ? null : Number(val) });
      toast.success(val === "" ? "Reset to standard" : "Estimate updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openStd = () => {
    if (data?.standards?.length) {
      setStds(data.standards.map((s) => ({ ...s })));
    } else if (data?.departments?.length) {
      setStds(data.departments.map((d) => ({ department: d, type: "fixed", value: 0 })));
    } else {
      setStds([]);
    }
    setStdOpen(true);
  };
  const saveStd = async () => {
    try {
      await api.put(`/properties/${propertyId}/labor-standards`, { labor_standards: stds });
      toast.success("Labor standards saved");
      setStdOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!propertyId) return null;

  return (
    <Card className="mt-6 rounded-none border p-6" data-testid="labor-comparison">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold"><Gauge className="h-5 w-5" /> Labor Standard vs Actual</h3>
          <p className="text-sm text-muted-foreground">Estimated department hours for this pay period, compared to actual worked hours.</p>
        </div>
        <div className="flex items-center gap-2">
          {data && lockedRef === undefined && (
            <div className="flex items-center gap-1 text-sm">
              <Button size="icon" variant="ghost" onClick={() => setRef(data.prev_ref)} data-testid="labor-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="min-w-[170px] text-center font-medium">{fmtDate(data.start)} – {fmtDate(data.end)}</span>
              <Button size="icon" variant="ghost" onClick={() => setRef(data.next_ref)} data-testid="labor-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
          {canEditStd && <Button variant="outline" size="sm" onClick={openStd} data-testid="labor-manage-std"><Settings className="mr-2 h-4 w-4" /> Manage Standards</Button>}
        </div>
      </div>

      {!data ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
      ) : data.rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No labor standards set for this property yet.{canEditStd && " Click \u201cManage Standards\u201d to add Front Desk, Housekeeping, Executive, etc."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="p-2">Department</th>
                <th className="p-2">Standard</th>
                <th className="p-2 text-right">Estimated hrs</th>
                <th className="p-2 text-right">Actual hrs</th>
                <th className="p-2 text-right">Variance</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const over = r.variance > 0.05;
                return (
                  <tr key={r.department} className="border-b" data-testid={`labor-row-${r.department}`}>
                    <td className="p-2 font-medium">{r.department}</td>
                    <td className="p-2 text-muted-foreground">{r.type === "per_room" ? `${r.value} min/room × ${data.rooms} rooms` : `${r.value} hrs fixed`}</td>
                    <td className="p-2 text-right">
                      {canOverride ? (
                        <Input
                          type="number"
                          className="ml-auto h-8 w-24 text-right"
                          value={edit[r.department] ?? r.estimated}
                          onChange={(e) => setEdit((m) => ({ ...m, [r.department]: e.target.value }))}
                          onBlur={(e) => { const v = e.target.value; if (Number(v) !== r.estimated) saveOverride(r.department, v); }}
                          data-testid={`labor-est-${r.department}`}
                        />
                      ) : r.estimated}
                      {r.is_override && <div className="text-[0.65rem] text-primary">override · <button className="underline" onClick={() => saveOverride(r.department, "")}>reset</button></div>}
                    </td>
                    <td className="p-2 text-right font-mono">{r.actual}</td>
                    <td className={`p-2 text-right font-mono font-semibold ${over ? "text-destructive" : "text-emerald-600"}`} data-testid={`labor-var-${r.department}`}>
                      {r.variance > 0 ? "+" : ""}{r.variance}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">Positive variance (red) = over the standard; negative (green) = under. Estimates are per pay period and can be overridden for occupancy.</p>
        </div>
      )}

      <Dialog open={stdOpen} onOpenChange={setStdOpen}>
        <DialogContent data-testid="labor-std-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Labor Standards</DialogTitle>
            <DialogDescription>Set the standard for each department. Fixed = hours per pay period; Per room = minutes per room (× occupancy).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {(data?.departments?.length > 0) && (
              <datalist id="labor-dept-list">
                {data.departments.map((d) => <option key={d} value={d} />)}
              </datalist>
            )}
            {stds.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input list="labor-dept-list" placeholder="Department" value={s.department} onChange={(e) => setStds((a) => a.map((x, j) => j === i ? { ...x, department: e.target.value } : x))} className="h-9 flex-1" data-testid={`std-dep-${i}`} />
                <Select value={s.type} onValueChange={(v) => setStds((a) => a.map((x, j) => j === i ? { ...x, type: v } : x))}>
                  <SelectTrigger className="h-9 w-36" data-testid={`std-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed hrs</SelectItem>
                    <SelectItem value="per_room">Min / room</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Value" value={s.value} onChange={(e) => setStds((a) => a.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} className="h-9 w-24" data-testid={`std-val-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => setStds((a) => a.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setStds((a) => [...a, { department: "", type: "fixed", value: 0 }])} data-testid="std-add"><Plus className="mr-2 h-4 w-4" /> Add department</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStdOpen(false)}>Cancel</Button>
            <Button onClick={saveStd} data-testid="std-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
